import { mapActions } from 'vuex';
import ConduitEventsService from '../services/conduitEventsService';

export default {
  props: [
    'layout',
    'rootItem',
    'item',
    'prev',
    'next',
    'index',
    'zoom',
    'grouped',
    'inContainer',
    'containerTimelineId',
  ],

  computed: {
    production() {
      return this.$store.state.production.production;
    },

    liveDisplayActive() {
      // TODO: Remove '0' check
      return !!(this.currentLayoutIndex && this.currentLayoutIndex !== '0');
    },

    currentLayoutIndex() {
      return this.$store.state.production.production.index;
    },

    currentPostIndex() {
      return this.$store.state.production.production.sub_index;
    },

    previewDisplayActive() {
      // TODO: Remove '0' check
      return !!(this.currentSampleLayoutIndex && this.currentSampleLayoutIndex !== '0');
    },

    currentSampleLayoutIndex() {
      return this.$store.state.preview.production.index;
    },

    rootLayout() {
      // This is why I dislike mixins... props mean different things based on the context
      return this.rootItem || this.layout || this.item || {};
    },

    isCurrentSampleLayout() {
      const { timeline_id: timelineId } = this.rootLayout;
      return timelineId === this.currentSampleLayoutIndex;
    },

    currentSamplePostIndex() {
      return this.$store.state.preview.production.sub_index;
    },

    isCurrentLayout() {
      const { timeline_id: timelineId } = this.rootLayout;
      return timelineId === this.currentLayoutIndex;
    },

    isCurrentPost() {
      const { timeline_id: timelineId } = this.rootLayout;

      return (
        this.item
        && timelineId === this.currentLayoutIndex
        && this.currentPostIndex === this.item.timeline_id
      );
    },

    isCurrentSamplePost() {
      const { timeline_id: timelineId } = this.rootLayout;

      return (
        this.item
        && timelineId === this.currentSampleLayoutIndex
        && this.currentSamplePostIndex === this.item.timeline_id
      );
    },

    videoPlaying() {
      return this.liveVideoPlaying || this.previewVideoPlaying;
    },

    liveVideoPlaying() {
      const { videoPlaying } = this.$store.state.production.production;
      return this.isCurrentPost && this.isCurrentLayout && videoPlaying;
    },

    previewVideoPlaying() {
      const { videoPlaying } = this.$store.state.preview.production;
      return this.isCurrentSamplePost && this.isCurrentSampleLayout && videoPlaying;
    },

    playing() {
      const { playing } = this.$store.state.production.production;
      return (this.isCurrentPost || this.isCurrentLayout) && playing;
    },

    editing() {
      return this.$store.state.timeline.editing;
    },

    isEditing() {
      return this.item && this.editing && (this.editing === this.item.timeline_id);
    },

    canDuplicateInGroup() {
      return Boolean(this.inContainer && this.containerTimelineId);
    },

    canMergeUp() {
      return this.canMergeWith(this.prev);
    },

    canMergeDown() {
      return this.canMergeWith(this.next);
    },

    displayTitle() {
      // Allow pretty names for multi worded panels types
      // can be of the following types: panel, item, post
      let type = 'Unknown type';

      if (this.item) {
        type = this.item.type;
      } else if (this.post) {
        type = this.post.type;
      } else if (this.panel) {
        type = this.panel.type;
      }

      return {
        'hashtag-battle': 'hashtag poll',
        'twitter-poll': 'twitter poll',
        'reddit-poll': 'reddit poll',
      }[type] || type;
    },
  },
  data() {
    return {
      segmentControlsEnabled: this.$ld?.has('enable-segment-controls', false),
    };
  },
  created() {
    this.$ld?.on(this.onChangeFlags);
  },
  beforeUnmount() {
    this.$ld?.off(this.onChangeFlags);
  },
  methods: {
    ...mapActions([
      'clearLayerEditing',
      'setLayerEditing',
    ]),

    onChangeFlags() {
      this.segmentControlsEnabled = this.$ld?.has('enable-segment-controls', false);
    },

    onClickTimelineItem() {
      if (this.isEditing) {
        // If we're already editing this item, just unselect it.
        this.clearLayerEditing();
      } else {
        // Otherwise we switch to the new item to edit.
        this.setLayerEditing(this.item.timeline_id);
      }
    },

    playPausePostVideo(opts = {}) {
      const {
        parent: parentTimelineId,
        item,
      } = opts;

      if (this.isCurrentPost) {
        this.$store.dispatch('toggleLiveVideo');
      } else {
        // Switch to new post if live, otherwise queue it for later.
        this.$store.dispatch('toggleLivePost', {
          parentTimelineId,
          item,
          post: item,
          eventSource: 'click',
        });

        if (this.isCurrentLayout) {
          this.$store.dispatch('toggleLiveVideo');
        }
      }

      if (this.isCurrentSamplePost) {
        this.$store.dispatch('togglePreviewVideo');
      } else {
        this.$store.dispatch('togglePreviewPost', {
          parentTimelineId,
          item,
          post: item,
          eventSource: 'click',
        });

        if (this.isCurrentSampleLayout) {
          this.$store.dispatch('togglePreviewVideo');
        }
      }
    },

    toggleVideo() {
      if (this.isCurrentLayout) {
        this.$store.dispatch('toggleLiveVideo');
      }

      if (this.isCurrentSampleLayout) {
        this.$store.dispatch('togglePreviewVideo');
      }
    },

    playVideo() {
      if(this.isCurrentLayout) {
        this.$store.dispatch('stopPostVideo');
        this.$store.dispatch('playPostVideo');
      }

      if (this.isCurrentSampleLayout) {
        this.$store.dispatch('stopPreviewVideo');
        this.$store.dispatch('playPreviewVideo');
      }
    },

    pauseVideo() {
      if(this.isCurrentLayout) {
        this.$store.dispatch('stopPostVideo');
      }

      if (this.isCurrentSampleLayout) {
        this.$store.dispatch('stopPreviewVideo');
      }
    },

    removeItem() {
      const { layout } = this.item;

      if (this.isEditing) {
        this.$store.dispatch('clearLayerEditing');
      }

      this.$store.dispatch('removeTimelineItem', { timelineId: this.item.timeline_id });
      this.$store.dispatch('removeTimelineItemPreview', { timelineId: this.item.timeline_id });

      ConduitEventsService.track(
        'producer.removed.item-from-playlist',
        {
          production_id: this.production._id,
          layout,
          item: this.item,
        },
        {
          ownerId: this.production.owner,
          userId: this.$store.state.user.id,
          isStaff: this.$store.getters.isStaff,
        },
      );
    },

    duplicateItem() {
      this.$store.dispatch('duplicateTimelineItem', { item: this.item });
    },

    duplicateItemInGroup() {
      if (!this.canDuplicateInGroup) {
        return;
      }

      this.$store.dispatch('duplicateTimelineItemInGroup', { item: this.item });
    },

    /**
     * Determine whether the adjacent timeline item can be merged with this one.
     * At the root of the timeline anything can merge into a graphics group
     * or a segment. Inside a container only graphics can merge, and only into
     * a graphics group. Markers are timeline controls and never merge.
     * @param {Object|null} neighbor - Adjacent timeline item (prev or next)
     * @returns {Boolean}
     */
    canMergeWith(neighbor) {
      if (!neighbor) {
        return false;
      }

      // Markers are timeline controls and can't be merged into groups or segments
      if (neighbor.layout === 'marker' || this.item?.layout === 'marker') {
        return false;
      }

      // Graphics nested inside a graphics group don't merge individually
      if (this.grouped) {
        return false;
      }

      if (this.inContainer && this.containerTimelineId) {
        // Inside a segment only graphics can merge, and only into a graphics group
        return ['graphic', 'graphics'].includes(this.item?.layout)
          && ['graphic', 'graphics'].includes(neighbor.layout);
      }

      // Graphics groups can always merge, but anything that would result in a
      // segment requires the segment controls flag
      const isGraphicsMerge = ['graphic', 'graphics'].includes(this.item?.layout)
        && ['graphic', 'graphics'].includes(neighbor.layout);

      if (!this.segmentControlsEnabled && !isGraphicsMerge) {
        return false;
      }

      // At the root of the timeline any two items can merge
      return true;
    },

    mergeUp() {
      const i1 = this.index - 1;
      const i2 = this.index;

      if (this.inContainer && this.containerTimelineId) {
        this.$store.dispatch('mergeGraphicsInContainer', {
          containerTimelineId: this.containerTimelineId,
          i1,
          i2,
        });

        return;
      }

      this.$store.dispatch('mergeTimelineItems', {
        i1,
        i2,
        sourceTimelineId: this.item?.timeline_id,
      });
    },

    mergeDown() {
      const i1 = this.index;
      const i2 = this.index + 1;

      if (this.inContainer && this.containerTimelineId) {
        this.$store.dispatch('mergeGraphicsInContainer', {
          containerTimelineId: this.containerTimelineId,
          i1,
          i2,
        });

        return;
      }

      this.$store.dispatch('mergeTimelineItems', {
        i1,
        i2,
        sourceTimelineId: this.item?.timeline_id,
      });
    },

    /**
     * Remove social post from theme.
     * @param {String} timelineId - Theme timeline ID
     * @param {String|Number} subIndex - Either the timeline_id or index of the post to remove
     */
    removeThemePost(opts = {}) {
      const { timelineId, subIndex } = opts;

      // Handle removing item via dropdown options
      if (this.$refs.dropdown) {
        // Make sure to hide this before removal since vue will reuse this component
        this.$refs.dropdown.hideDropdown();
      }

      this.$store.dispatch('removeThemePost', {
        index: timelineId,
        sub_index: subIndex,
      });
    },

    normalizeType(layout){
      return {
        'graphic': 'Graphic',
        'panel':   'Panel',
        'theme':   'Theme',
      }[layout] || 'Theme';
    },
  },
};
